import { Box, Checkbox, Divider, Group, LoadingOverlay, Popover, Space, Switch } from '@mantine/core';
import { observer, useObserver } from 'mobx-react';
import { RuleEditCard } from './Design';
import { RuleEditor, TagLookupService } from './Model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnchorButton, Clearfix } from '@root/Design/Primitives';
import { FilterResources, ValidationIssues } from './FilterResources';
import { DataGrid } from '@root/Components/DataGrid';
import { ColumnConfig } from '@root/Components/DataGrid/Models';
import { TypeMapping } from '@apis/Resources/model';
import { DataGridModel } from '@root/Components/DataGrid/DataGridModel';
import { useEvent } from '@root/Services/EventEmitter';
import { injectable } from 'tsyringe';
import {
    HierarchyOptions,
    TagAutomationRule,
    TagAutomationRuleParametersType,
    TagRuleSyntax,
    TagAutomationRuleParameters,
} from '@apis/TagManager/model';
import { useFilterValidation } from './RuleDescriber';
import { QueryExpr, postResourceTagAutomationGetFilteredResourceTypes, postResourceTagAutomationGetInheritancePreviewCount } from '@apis/Resources';
import { makeAutoObservable } from 'mobx';
import { Tag } from 'tabler-icons-react';
import { Picker } from '@root/Components/Picker/Picker';
import { useDisclosure } from '@mantine/hooks';
import { useDi } from '@root/Services/DI';
import { useId } from '@root/Services/IdGen';
import { AnnotatedTagKey } from '@root/Components/Resources/Common/ResourceTags';

interface InheritanceState {
    inheritableTypes: TypeMapping[];
    inheritableTypesLoading: boolean;
}

@injectable()
export class InheritanceEditor {
    public options: HierarchyOptions = {};
    public syntax: TagRuleSyntax = {};
    public ruleEditor: RuleEditor = {} as RuleEditor;
    public rule?: TagAutomationRule = undefined;
    public constructor() {
        makeAutoObservable(this);
    }
    public init(ruleEditor: RuleEditor) {
        const rule = ruleEditor.rule!;
        rule.Parameters ??= {};
        rule.Parameters.Syntax ??= {};
        rule.Parameters.Syntax.HierarchyOptions ??= {};
        rule.Parameters.Syntax.HierarchyOptions.ChildResourceTypes ??= [];
        rule.Parameters.Filter ??= { Operation: 'and', Operands: [] };
        rule.Parameters.Syntax.SyntaxOptions ??= {};
        rule.Parameters.Syntax.SyntaxOptions.TagKeysToApplyTo ??= [];
        this.options = rule.Parameters.Syntax.HierarchyOptions;
        this.syntax = rule.Parameters.Syntax;
        this.ruleEditor = ruleEditor;
        this.rule = rule;
        return this;
    }

    public inheritanceState: InheritanceState = {
        inheritableTypes: [],
        inheritableTypesLoading: false,
    };

    public async loadFilteredResources() {
        if (!this.rule) {
            return;
        }
        this.inheritanceState.inheritableTypesLoading = true;
        const rule = this.ruleEditor.trimFilter(this.rule);
        await postResourceTagAutomationGetFilteredResourceTypes(rule)
            .then((response) => {
                this.inheritanceState.inheritableTypes = response.ChildTypes ?? [];
            })
            .catch((error) => {
                this.inheritanceState.inheritableTypes = [];
            })
            .finally(() => {
                this.inheritanceState.inheritableTypesLoading = false;
                this.ruleEditor.setTypeErrors(TagAutomationRuleParametersType.Inheritance, ['You must select at least one child resource type.']);
            });
    }

    public setChildResourceTypes(types: TypeMapping[]) {
        this.options.ChildResourceTypes = types;
    }

    public getErrors() {
        const result: string[] = [];
        return result;
    }
}

export const InheritanceActionCard = observer(function InheritanceActionCard({ inheritanceEditor }: { inheritanceEditor: InheritanceEditor }) {
    const [grid, setGrid] = useState<DataGridModel>();
    const syntaxOptions = useMemo(() => {
        inheritanceEditor.rule ??= {};
        inheritanceEditor.rule.Parameters ??= {};
        inheritanceEditor.rule.Parameters.Syntax ??= {};
        inheritanceEditor.rule.Parameters.Syntax.SyntaxOptions ??= {};
        return inheritanceEditor.rule.Parameters.Syntax.SyntaxOptions;
    }, [inheritanceEditor.rule]);
    const [keyPickerOpened, { close: closeKeyPicker, toggle: toggleKeyPicker }] = useDisclosure(false);
    const tagLookupSvc = useDi(TagLookupService);
    const keyPickerOptions: string[] = tagLookupSvc.keys;
    const keyPickerDescription = useMemo(() => {
        const count = syntaxOptions.TagKeysToApplyTo?.length ?? 0;
        return !count ? 'Copy All Tags' : `Copy ${count} selected tag${count > 1 ? 's' : ''}`;
    }, [syntaxOptions.TagKeysToApplyTo?.length, syntaxOptions.ApplyTagKeys]);

    const clearKeyPicker = useCallback(() => {
        syntaxOptions.TagKeysToApplyTo?.splice(0, Infinity);
        closeKeyPicker();
    }, [closeKeyPicker, syntaxOptions]);

    useEvent(grid?.selectionChanged, async () => {
        const selectedResourceTypes = (await grid?.selections.getSelected()) as TypeMapping[];
        if (selectedResourceTypes != null || selectedResourceTypes != undefined) {
            inheritanceEditor.setChildResourceTypes(selectedResourceTypes);
        }
    });

    const onChange = useCallback(() => {
        const issues: string[] = [];
        const options = inheritanceEditor.rule?.Parameters?.Syntax?.HierarchyOptions;
        if (!options?.ChildResourceTypes?.length) {
            issues.push('You must select at least one child resource type.');
        }
        inheritanceEditor.ruleEditor.setTypeErrors(TagAutomationRuleParametersType.Inheritance, issues);
    }, [inheritanceEditor.ruleEditor]);
    useObserver(onChange);

    useEffect(() => {
        (async () => {
            await inheritanceEditor.loadFilteredResources();
        })();
    }, []);

    const filterValidityChanged = useFilterValidation(inheritanceEditor.ruleEditor);

    const columnResult: ColumnConfig<TypeMapping>[] = [
        {
            accessor: (item) => item.ParentResourceType,
            id: 'ParentResourceType',
            sortField: 'ParentResourceType',
            header: 'Source Resource Type',
            defaultWidth: 250,
            defaultFixed: true,
            type: 'string',
        },
        {
            accessor: (item) => item.ChildResourceType,
            id: 'ChildResourceType',
            sortField: 'ChildResourceType',
            header: 'Attached Resource Type',
            defaultWidth: 250,
            defaultFixed: true,
            type: 'string',
        },
    ];

    const dataGridKey = useId(inheritanceEditor.inheritanceState.inheritableTypes);
    return (
        <>
            <RuleEditCard
                accent
                title="Parent Resource Filter"
                description="Add filters to identify which parent resources have tags that should be inherited by their child resources"
            >
                {inheritanceEditor.rule ? (
                    <>
                        <InheritanceRuleResourceFilter typeEditor={inheritanceEditor} onValidationIssuesChanged={filterValidityChanged} />
                    </>
                ) : null}
            </RuleEditCard>
            <Space h="md" />
            {inheritanceEditor.inheritanceState.inheritableTypes.length > 0 ? (
                <>
                    {inheritanceEditor.inheritanceState.inheritableTypesLoading ? (
                        <LoadingOverlay visible />
                    ) : (
                        <>
                            <div style={{ width: '66%', float: 'left' }}>
                                {' '}
                                <RuleEditCard title="Select Attached Resource Types">
                                    <div style={{ height: 400 }}>
                                        <DataGrid
                                            key={dataGridKey}
                                            dataSource={inheritanceEditor.inheritanceState.inheritableTypes}
                                            selectionMode={'multiple'}
                                            initialSelection={inheritanceEditor.inheritanceState.inheritableTypes.filter((x) => {
                                                return (
                                                    inheritanceEditor.options.ChildResourceTypes?.find(
                                                        (y) => y.ChildResourceType == x.ChildResourceType
                                                    ) != null
                                                );
                                            })}
                                            showHeaderGroups={true}
                                            columns={columnResult}
                                            hideMenu={true}
                                            showRefresh
                                            hideFilter={true}
                                            hideGlobalSearch={true}
                                            onModelLoaded={setGrid}
                                        />
                                    </div>
                                </RuleEditCard>
                            </div>
                            <div style={{ width: '32.3%', float: 'left', marginLeft: '1.7%' }}>
                                <RuleEditCard title="Additional Options">
                                    <Group>
                                        <Box>
                                            <Group>
                                                <Box pt="sm" sx={{ minHeight: '42px' }}>
                                                    <Popover
                                                        opened={keyPickerOpened}
                                                        onClose={closeKeyPicker}
                                                        withinPortal
                                                        shadow="lg"
                                                        offset={0}
                                                        position="bottom"
                                                        withArrow
                                                    >
                                                        <Popover.Target>
                                                            <AnchorButton text={keyPickerDescription} onClick={toggleKeyPicker} icon={<Tag />} />
                                                        </Popover.Target>
                                                        <Popover.Dropdown p={0}>
                                                            <Picker
                                                                height={200}
                                                                width={300}
                                                                items={keyPickerOptions}
                                                                onChange={(c) => (syntaxOptions.TagKeysToApplyTo = c)}
                                                                onFilterChange={(c) => {
                                                                    tagLookupSvc.getTagKeysWithFilter(c);
                                                                }}
                                                                selections={(syntaxOptions.TagKeysToApplyTo ??= [])}
                                                                childAccessor={() => undefined}
                                                                nameAccessor={(v) => v}
                                                                renderItem={(v) => (
                                                                    <AnnotatedTagKey tagKey={v} restrictedTags={tagLookupSvc.restrictedTags} />
                                                                )}
                                                                bubbleUpSelections={true}
                                                            />
                                                            <Divider />
                                                            <Checkbox
                                                                m="sm"
                                                                disabled={!syntaxOptions.TagKeysToApplyTo?.length}
                                                                label="All tag keys"
                                                                checked={!syntaxOptions.TagKeysToApplyTo?.length}
                                                                onClick={clearKeyPicker}
                                                            />
                                                        </Popover.Dropdown>
                                                    </Popover>
                                                </Box>
                                            </Group>
                                            <Box pt="sm" sx={{ minHeight: '42px' }}>
                                                <Switch
                                                    label="Overwrite existing tags"
                                                    checked={inheritanceEditor.syntax.OverwriteValue ?? false}
                                                    onChange={(v) => (inheritanceEditor.syntax.OverwriteValue = v.target.checked)}
                                                />
                                            </Box>
                                        </Box>
                                    </Group>
                                </RuleEditCard>
                            </div>
                            <Clearfix></Clearfix>
                        </>
                    )}
                </>
            ) : null}
        </>
    );
});

//had to make a copy for the inheritance rule because the parent filter is in a different place on the object and we don't care about the main filter
export function InheritanceRuleResourceFilter({
    typeEditor,
    onValidationIssuesChanged,
}: {
    typeEditor: InheritanceEditor;
    onValidationIssuesChanged: (issues: ValidationIssues) => void;
}) {
    let rule: TagAutomationRule = typeEditor.rule!;
    const normalizeFilter = (rule: TagAutomationRule) => {
        let result: QueryExpr[];
        rule.Parameters ??= {};
        rule.Parameters.Syntax ??= {};
        rule.Parameters.Syntax.HierarchyOptions ??= {};
        rule.Parameters.Syntax.HierarchyOptions.ParentFilter ??= { Operation: 'and', Operands: (result = []) };
        if (!('Operation' in rule.Parameters.Syntax.HierarchyOptions.ParentFilter)) {
            rule.Parameters.Filter = { Opearation: 'and', Operands: (result = []) };
        } else {
            result = rule.Parameters.Syntax.HierarchyOptions.ParentFilter.Operands ??= [];
        }
        return result;
    };
    const filter = useMemo(() => normalizeFilter(rule), [rule, rule.Parameters?.Syntax?.HierarchyOptions?.ParentFilter]);
    const onChange = useCallback((nextFilter: QueryExpr[]) => {
        filter.splice(0, Infinity, ...nextFilter);
        (async () => {
            await typeEditor.loadFilteredResources();
        })();
    }, []);
    return <FilterResources filter={filter} onChange={onChange} onValidationIssuesChanged={onValidationIssuesChanged} />;
}
